// SPDX-License-Identifier: AGPL-3.0-or-later

import type {Channel} from '@app/features/channel/models/Channel';
import {SoundType} from '@app/features/notification/utils/SoundUtils';
import * as TableGameCommands from '@app/features/table_game/commands/TableGameCommands';
import {autoAddStage, mayAdd, meldAddition, meldAdditions} from '@app/features/table_game/components/okey/Okey101Fit';
import {Okey101Melds, Okey101Scores, Okey101SeatBadges} from '@app/features/table_game/components/okey/Okey101Melds';
import {type OkeyStage, stagedTiles, stageToAction} from '@app/features/table_game/components/okey/Okey101Staging';
import styles from '@app/features/table_game/components/okey/OkeyBoard.module.css';
import {OkeyRack} from '@app/features/table_game/components/okey/OkeyRack';
import {
	layoutRack,
	moveRackTile,
	type OkeyFace,
	okeyFaceOf,
	RACK_CELLS,
	rackGroups,
	trimRack,
} from '@app/features/table_game/components/okey/OkeyRackModel';
import {OkeyTile} from '@app/features/table_game/components/okey/OkeyTile';
import {useClockNow} from '@app/features/table_game/hooks/useClockNow';
import {playTableUiSound} from '@app/features/table_game/sound/TableGameSounds';
import TableGames, {type RackSlot} from '@app/features/table_game/state/TableGames';
import {tableDraftKey} from '@app/features/table_game/utils/TableDraftKey';
import {tableGameOutcomeDescriptor} from '@app/features/table_game/utils/TableGameMessages';
import {Button} from '@app/features/ui/button/Button';
import Users from '@app/features/user/state/Users';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {TABLE_GAME_TURN_MS} from '@fluxer/constants/src/TableGameConstants';
import type {
	Okey101View,
	OkeyView,
	TableGameAction,
	TableGameSummary,
} from '@fluxer/schema/src/domains/table_game/TableGameSchemas';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useMemo, useState} from 'react';

type OkeyGameView = OkeyView | Okey101View;

const TILES_DESCRIPTOR = msg({
	message: '{count} tiles',
	comment: 'Table games: Okey, how many tiles a player holds; the tiles themselves stay hidden. {count} is a number.',
});
const PILE_DESCRIPTOR = msg({
	message: '{count} in the pile',
	comment: 'Table games: Okey, tiles left to draw. {count} is a number.',
});
const INDICATOR_DESCRIPTOR = msg({
	message: 'Indicator',
	comment: 'Table games: Okey, the face-up tile that decides the okey (one higher, same color).',
});
const DEADLINE_DESCRIPTOR = msg({
	message: '{seconds} seconds left',
	comment: "Table games: Okey, time left in the current player's turn. {seconds} is a number.",
});
const DRAW_PILE_DESCRIPTOR = msg({
	message: 'Draw from pile',
	comment: 'Table games: Okey, take the top tile of the face-down pile.',
});
const TAKE_SIDE_DESCRIPTOR = msg({
	message: 'Take from side',
	comment: 'Table games: Okey, take the tile the previous player just discarded.',
});
const PUT_BACK_DESCRIPTOR = msg({
	message: 'Put back',
	comment: 'Table games: 101 Okey, give back the tile taken from the side before using it.',
});
const DISCARD_DESCRIPTOR = msg({
	message: 'Discard',
	comment: 'Table games: Okey, throw the selected tile to end the turn.',
});
const FINISH_DESCRIPTOR = msg({
	message: 'Finish',
	comment: 'Table games: classic Okey, end the hand: throw the selected tile and show the rest as sets.',
});
const OPEN_DESCRIPTOR = msg({
	message: 'Open',
	comment: 'Table games: 101 Okey, lay the selected sets on the table.',
});
const AUTO_ADD_DESCRIPTOR = msg({
	message: 'Auto add',
	comment:
		'Table games: 101 Okey, place every rack tile that fits a set on the table onto that set in one tap (confirmed with Add, dropped with Cancel).',
});
const ADD_STAGED_DESCRIPTOR = msg({
	message: 'Add ({count})',
	comment: 'Table games: 101 Okey, send the tiles placed onto sets by Auto add; {count} is how many tiles.',
});
const CANCEL_STAGED_DESCRIPTOR = msg({
	message: 'Cancel',
	context: 'okey-board',
	comment: 'Table games: 101 Okey, drop the tiles placed onto sets by Auto add; they go back to the rack.',
});
const RACK_HINT_DESCRIPTOR = msg({
	message: 'Leave a gap between sets. Runs go from low to high.',
	comment: 'Table games: Okey, how the rack is read when opening or finishing.',
});

// Classic Okey finishes from 15 tiles: 14 in sets and the one thrown.
const CLASSIC_FINISH_RACK_SIZE = 15;
const DEADLINE_LOW_MS = 5000;
const INVALID_MELD_DESCRIPTOR = tableGameOutcomeDescriptor({ok: false, error: 'rule', reason: 'invalid_meld'});
const NO_FITS: ReadonlySet<number> = new Set();
const CANNOT_ADD_DESCRIPTOR = tableGameOutcomeDescriptor({ok: false, error: 'rule', reason: 'cannot_add'});

interface Taking {
	key: string;
	tile: string;
	cell: number;
}

// Sets in the shape the server accepts (2 to 13 tiles each); anything else is refused here without a request.
function validSets(sets: ReadonlyArray<ReadonlyArray<string>>, maxSets: number): boolean {
	return sets.length > 0 && sets.length <= maxSets && sets.every((set) => set.length >= 2 && set.length <= 13);
}

function placeTaken(cells: ReadonlyArray<RackSlot>, taking: Taking): Array<RackSlot> {
	const next = [...cells];
	next[next[taking.cell] === null ? taking.cell : next.indexOf(null)] = taking.tile;
	return next;
}

// Shown only on the seat whose turn runs; it redraws itself every second, the board and the seats do not.
function DeadlineRing({deadlineAt, turnMs}: {deadlineAt: number; turnMs: number}) {
	const {i18n} = useLingui();
	const now = useClockNow(true, 1000);
	const remaining = Math.max(0, deadlineAt - now);
	const seconds = Math.ceil(remaining / 1000);
	return (
		<span
			className={styles.deadline}
			role="timer"
			aria-label={i18n._(DEADLINE_DESCRIPTOR, {seconds})}
			data-deadline="true"
			data-low={remaining < DEADLINE_LOW_MS ? 'true' : undefined}
			data-flx="table-game.okey.okey-board.deadline-ring.deadline"
		>
			<svg
				className={styles.ring}
				viewBox="0 0 36 36"
				aria-hidden
				data-flx="table-game.okey.okey-board.deadline-ring.ring"
			>
				<circle
					className={styles.ringTrack}
					cx="18"
					cy="18"
					r="16"
					data-flx="table-game.okey.okey-board.deadline-ring.ring-track"
				/>
				<circle
					className={styles.ringFill}
					cx="18"
					cy="18"
					r="16"
					pathLength={1}
					style={{strokeDashoffset: 1 - Math.min(1, remaining / turnMs)}}
					data-flx="table-game.okey.okey-board.deadline-ring.ring-fill"
				/>
			</svg>
			<span className={styles.seconds} data-flx="table-game.okey.okey-board.deadline-ring.seconds">
				{seconds}
			</span>
		</span>
	);
}

// One player around the table: name, turn clock, how many tiles (never which), last discard, 101 badges.
function Seat({
	summary,
	view,
	position,
	name,
	okey,
}: {
	summary: TableGameSummary;
	view: OkeyGameView;
	position: number;
	name: string;
	okey: OkeyFace | null;
}) {
	const {i18n} = useLingui();
	const onTurn = summary.status === 'playing' && view.turn === position;
	const deadlineAt = onTurn && view.deadline_at ? Date.parse(view.deadline_at) : null;
	const lastDiscard = view.discards[position - 1]?.at(-1) ?? null;
	const count = view.rack_counts[position - 1] ?? 0;
	const connected = summary.players.find((player) => player.position === position)?.connected ?? true;
	return (
		<div
			className={styles.seat}
			data-seat-position={position}
			data-turn={onTurn ? 'true' : undefined}
			data-connected={connected ? undefined : 'false'}
			data-flx="table-game.okey.okey-board.seat.seat"
		>
			<div className={styles.seatHead} data-flx="table-game.okey.okey-board.seat.head">
				<span className={styles.seatName} data-flx="table-game.okey.okey-board.seat.name">
					{name}
				</span>
				{deadlineAt !== null && (
					<DeadlineRing
						deadlineAt={deadlineAt}
						turnMs={TABLE_GAME_TURN_MS[view.game][summary.speed]}
						data-flx="table-game.okey.okey-board.seat.deadline-ring"
					/>
				)}
			</div>
			<div className={styles.seatBody} data-flx="table-game.okey.okey-board.seat.body">
				<span className={styles.count} data-rack-count={count} data-flx="table-game.okey.okey-board.seat.count">
					{i18n._(TILES_DESCRIPTOR, {count})}
				</span>
				{lastDiscard && (
					<OkeyTile token={lastDiscard} okey={okey} size="small" data-flx="table-game.okey.okey-board.seat.okey-tile" />
				)}
			</div>
			{view.game === 'okey101' && (
				<Okey101SeatBadges
					view={view}
					position={position}
					data-flx="table-game.okey.okey-board.seat.okey101-seat-badges"
				/>
			)}
		</div>
	);
}

// Classic Okey and 101 Okey for one table. Only the viewer's own rack is ever known here (the server sends no
// other); the rest of the board is the public view every player and spectator gets.
export const OkeyBoard = observer(function OkeyBoard({channel, slot}: {channel: Channel; slot: string}) {
	const {i18n} = useLingui();
	const view = TableGames.getView(channel.id, slot);
	const order = TableGames.getRack(channel.id, slot);
	const [picked, setPicked] = useState<ReadonlyArray<string>>([]);
	const [taking, setTaking] = useState<Taking | null>(null);
	// 101 tiles staged onto sets by "Otomatik işle", for the draft they were made on.
	const [staged, setStaged] = useState<{key: string; stage: OkeyStage} | null>(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<MessageDescriptor | null>(null);
	const gameView = view?.game_view?.game === 'okey' || view?.game_view?.game === 'okey101' ? view.game_view : null;
	const stage = view && staged?.key === tableDraftKey(view) ? staged.stage : null;
	const game101 = gameView?.game === 'okey101' ? gameView : null;
	const viewer = view?.viewer_position ?? null;
	// The fit work reruns when the table, the stage or the picked tiles change, not on every clock tick.
	const autoStage = useMemo(
		() => (game101 && viewer !== null ? autoAddStage(game101, viewer, stage) : null),
		[game101, viewer, stage],
	);
	const fitting = useMemo(() => {
		const inDraft = stagedTiles(stage);
		const tiles = picked.filter(
			(tile) => !inDraft.includes(tile) && (game101?.rack?.includes(tile) || tile === taking?.tile),
		);
		if (!game101 || tiles.length === 0) return NO_FITS;
		return new Set(
			game101.melds.filter((meld) => meldAdditions(game101, stage, tiles, meld.id) !== null).map((meld) => meld.id),
		);
	}, [game101, stage, picked, taking]);
	if (!view || !gameView) return null;

	const summary = view.summary;
	// Drafts live until a move, not a version bump.
	const key = tableDraftKey(view);
	const classic = gameView.game === 'okey';
	const me = view.viewer_position;
	const bottom = me ?? 1;
	const rack = me === null ? null : gameView.rack;
	const myTurn = rack !== null && summary.status === 'playing' && gameView.turn === me;
	const previous = ((bottom + 2) % 4) + 1;
	const offered = gameView.discards[previous - 1]?.at(-1) ?? null;
	const okey = okeyFaceOf(gameView.indicator);
	const names = [1, 2, 3, 4].map((position) => {
		const userId = summary.players.find((player) => player.position === position)?.user_id;
		const user = userId ? Users.getUser(userId) : undefined;
		return user ? NicknameUtils.getNickname(user, channel.guildId) : '';
	});
	const nameOf = (position: number) => names[position - 1] ?? '';

	// 101: the side tile sits in the rack (not yet the player's) until it is laid down in the same action.
	const live = taking && taking.key === key && myTurn && gameView.phase === 'draw' ? taking : null;
	const base = layoutRack(order);
	const cells = live ? placeTaken(base, live) : base;
	const selected = new Set(picked.filter((tile) => cells.includes(tile)));
	const selectedInOrder = cells.filter((tile): tile is string => tile !== null && selected.has(tile));

	const send = (action: TableGameAction) => {
		setBusy(true);
		setError(null);
		void TableGameCommands.act(channel.id, slot, action).then((outcome) => {
			setBusy(false);
			setError(tableGameOutcomeDescriptor(outcome));
			if (outcome.ok) setPicked([]);
		});
	};

	const move = (from: number, to: number) => {
		const next = moveRackTile(cells, from, to);
		if (live) {
			setTaking({...live, cell: next.indexOf(live.tile)});
			TableGames.setRackOrder(channel.id, slot, trimRack(next.map((tile) => (tile === live.tile ? null : tile))));
			return;
		}
		TableGames.setRackOrder(channel.id, slot, trimRack(next));
	};

	const onCellClick = (index: number) => {
		setError(null);
		const tile = cells[index] ?? null;
		if (tile === null) {
			if (selectedInOrder.length === 1) {
				playTableUiSound(SoundType.TableGameTilePlace);
				move(cells.indexOf(selectedInOrder[0]!), index);
				setPicked([]);
			}
			return;
		}
		if (selected.has(tile)) setPicked(selectedInOrder.filter((other) => other !== tile));
		else setPicked(classic ? [tile] : [...selectedInOrder, tile]);
	};

	const takeFromSide = () => {
		if (!offered) return;
		const last = base.reduce((found, tile, index) => (tile === null ? found : index), -1);
		const cell = last + 1 < RACK_CELLS ? last + 1 : base.indexOf(null);
		setTaking({key, tile: offered, cell});
		setPicked([offered]);
		setError(null);
	};

	const openSets = () => {
		const sets = rackGroups(cells)
			.map((group) => group.filter((tile) => selected.has(tile)))
			.filter((group) => group.length > 0);
		if (!validSets(sets, 12)) {
			setError(INVALID_MELD_DESCRIPTOR);
			return;
		}
		send(live ? {type: 'take_discard', open: sets} : {type: 'meld', open: sets});
	};

	const inStage = stagedTiles(stage);

	// Tiles onto a set, each at the end it fits; with a stage on the table they join it instead of going out alone.
	const addTiles = (meldId: number, tiles: ReadonlyArray<string>) => {
		if (gameView.game !== 'okey101') return;
		const add = meldAdditions(gameView, stage, tiles, meldId);
		if (!add) {
			setError(CANNOT_ADD_DESCRIPTOR);
			return;
		}
		setError(null);
		setPicked([]);
		playTableUiSound(SoundType.TableGameTilePlace);
		if (stage) setStaged({key, stage: {...stage, add: [...stage.add, ...add]}});
		else send(live ? {type: 'take_discard', add} : {type: 'meld', add});
	};

	const addTo = (meldId: number, at: 'start' | 'end') => {
		if (stage && gameView.game === 'okey101') {
			// Joins the stage, one tile at a time at that end (at the start the last one goes first, keeping their order).
			let next = stage;
			const tiles = selectedInOrder.filter((tile) => !inStage.includes(tile));
			for (const tile of at === 'start' ? [...tiles].reverse() : tiles) {
				const addition = meldAddition(gameView, next, tile, meldId, at);
				if (!addition) {
					setError(CANNOT_ADD_DESCRIPTOR);
					return;
				}
				next = {...next, add: [...next.add, addition]};
			}
			setError(null);
			setPicked([]);
			setStaged({key, stage: next});
			return;
		}
		const add = [{meld_id: meldId, tiles: selectedInOrder, at}];
		send(live ? {type: 'take_discard', add} : {type: 'meld', add});
	};

	const finish = () => {
		const tile = selectedInOrder[0]!;
		const sets = rackGroups(cells, tile);
		if (!validSets(sets, 7)) {
			setError(INVALID_MELD_DESCRIPTOR);
			return;
		}
		send({type: 'finish', tile, sets});
	};

	const drawPhase = myTurn && gameView.phase === 'draw' && !live && !busy;
	const canDrawPile = drawPhase && gameView.pile_count > 0;
	const canTakeSide = drawPhase && offered !== null;
	const throwPhase = myTurn && gameView.phase === (classic ? 'discard' : 'play') && !live && !busy;
	const canDiscard = throwPhase && selected.size === 1 && stage === null;
	const canFinish = canDiscard && rack?.length === CLASSIC_FINISH_RACK_SIZE;
	const laying = !busy && (live !== null || (myTurn && gameView.phase === 'play'));
	const canOpen = laying && selected.size > 0;
	const opened = gameView.game === 'okey101' && me !== null && mayAdd(gameView, me);
	const canAdd = laying && opened && selected.size >= 1 && selected.size <= 12;
	const picking = selectedInOrder.filter((tile) => !inStage.includes(tile));
	const fittingShown = laying && opened ? fitting : NO_FITS;
	const autoShown = myTurn && !busy && !live ? autoStage : null;
	const dropOnMeld = (meldId: number, index: number) => {
		const tile = cells[index];
		if (laying && opened && tile && !inStage.includes(tile)) addTiles(meldId, [tile]);
	};
	const sideTile = offered && (
		<OkeyTile token={offered} okey={okey} size="small" data-flx="table-game.okey.okey-board.okey-tile" />
	);

	return (
		<div className={styles.board} data-game={gameView.game} data-flx="table-game.okey.okey-board.board">
			<div className={styles.opponents} data-flx="table-game.okey.okey-board.opponents">
				{[3, 2, 1].map((offset) => {
					const position = ((bottom - 1 + offset) % 4) + 1;
					return (
						<Seat
							key={position}
							summary={summary}
							view={gameView}
							position={position}
							name={nameOf(position)}
							okey={okey}
							data-flx="table-game.okey.okey-board.seat"
						/>
					);
				})}
			</div>
			<div className={styles.center} data-flx="table-game.okey.okey-board.center">
				<div className={styles.indicator} data-indicator="true" data-flx="table-game.okey.okey-board.indicator">
					<span className={styles.centerLabel} data-flx="table-game.okey.okey-board.indicator-label">
						{i18n._(INDICATOR_DESCRIPTOR)}
					</span>
					<OkeyTile
						token={gameView.indicator}
						okey={okey}
						size="small"
						data-flx="table-game.okey.okey-board.okey-tile--2"
					/>
				</div>
				<span className={styles.pile} data-pile-count={gameView.pile_count} data-flx="table-game.okey.okey-board.pile">
					{i18n._(PILE_DESCRIPTOR, {count: gameView.pile_count})}
				</span>
			</div>
			{gameView.game === 'okey101' && (
				<Okey101Melds
					view={gameView}
					okey={okey}
					nameOf={nameOf}
					canAdd={canAdd}
					fitting={fittingShown}
					stage={stage}
					onAdd={rack !== null && summary.status === 'playing' ? addTo : null}
					onPick={(meldId) => addTiles(meldId, picking)}
					onDropTile={rack !== null && summary.status === 'playing' ? dropOnMeld : null}
					data-flx="table-game.okey.okey-board.okey101-melds"
				/>
			)}
			<Seat
				summary={summary}
				view={gameView}
				position={bottom}
				name={nameOf(bottom)}
				okey={okey}
				data-flx="table-game.okey.okey-board.seat--2"
			/>
			{rack !== null && (
				<OkeyRack
					cells={cells}
					okey={okey}
					selected={selected}
					pending={live?.tile ?? null}
					onCellClick={onCellClick}
					onMove={move}
					data-flx="table-game.okey.okey-board.okey-rack"
				/>
			)}
			{rack !== null && summary.status === 'playing' && (
				<>
					<div className={styles.actions} data-flx="table-game.okey.okey-board.actions">
						<Button
							small
							variant="secondary"
							disabled={!canDrawPile}
							onClick={() => send({type: 'draw', from: 'pile'})}
							data-action="draw-pile"
							data-flx="table-game.okey.okey-board.button.draw-pile"
						>
							{i18n._(DRAW_PILE_DESCRIPTOR)}
						</Button>
						{classic ? (
							<Button
								small
								variant="secondary"
								disabled={!canTakeSide}
								onClick={() => send({type: 'draw', from: 'discard'})}
								data-action="draw-discard"
								data-flx="table-game.okey.okey-board.button.draw-discard"
							>
								<span className={styles.withTile} data-flx="table-game.okey.okey-board.draw-discard-label">
									{i18n._(TAKE_SIDE_DESCRIPTOR)}
									{sideTile}
								</span>
							</Button>
						) : live ? (
							<Button
								small
								variant="secondary"
								onClick={() => {
									setTaking(null);
									setPicked([]);
								}}
								data-action="take-cancel"
								data-flx="table-game.okey.okey-board.button.take-cancel"
							>
								{i18n._(PUT_BACK_DESCRIPTOR)}
							</Button>
						) : (
							<Button
								small
								variant="secondary"
								disabled={!canTakeSide}
								onClick={takeFromSide}
								data-action="take-discard"
								data-flx="table-game.okey.okey-board.button.take-discard"
							>
								<span className={styles.withTile} data-flx="table-game.okey.okey-board.take-discard-label">
									{i18n._(TAKE_SIDE_DESCRIPTOR)}
									{sideTile}
								</span>
							</Button>
						)}
						{!classic && (
							<Button
								small
								disabled={!canOpen}
								onClick={openSets}
								data-action="open"
								data-flx="table-game.okey.okey-board.button.open"
							>
								{i18n._(OPEN_DESCRIPTOR)}
							</Button>
						)}
						{stage && (
							<>
								<Button
									small
									disabled={busy}
									onClick={() => send(stageToAction(stage))}
									data-action="stage-confirm"
									data-flx="table-game.okey.okey-board.button.stage-confirm"
								>
									{i18n._(ADD_STAGED_DESCRIPTOR, {count: inStage.length})}
								</Button>
								<Button
									small
									variant="secondary"
									onClick={() => setStaged(null)}
									data-action="stage-cancel"
									data-flx="table-game.okey.okey-board.button.stage-cancel"
								>
									{i18n._(CANCEL_STAGED_DESCRIPTOR)}
								</Button>
							</>
						)}
						{autoShown && (
							<Button
								small
								variant={stage ? 'secondary' : 'primary'}
								onClick={() => {
									setStaged({key, stage: autoShown});
									setPicked([]);
									setError(null);
								}}
								data-action="auto-add"
								data-flx="table-game.okey.okey-board.button.auto-add"
							>
								{i18n._(AUTO_ADD_DESCRIPTOR)}
							</Button>
						)}
						<Button
							small
							disabled={!canDiscard}
							onClick={() => send({type: 'discard', tile: selectedInOrder[0]!})}
							data-action="discard"
							data-flx="table-game.okey.okey-board.button.discard"
						>
							{i18n._(DISCARD_DESCRIPTOR)}
						</Button>
						{classic && (
							<Button
								small
								disabled={!canFinish}
								onClick={finish}
								data-action="finish"
								data-flx="table-game.okey.okey-board.button.finish"
							>
								{i18n._(FINISH_DESCRIPTOR)}
							</Button>
						)}
					</div>
					<p className={styles.hint} data-flx="table-game.okey.okey-board.hint">
						{i18n._(RACK_HINT_DESCRIPTOR)}
					</p>
				</>
			)}
			{error && (
				<p className={styles.error} role="alert" data-flx="table-game.okey.okey-board.error">
					{i18n._(error)}
				</p>
			)}
			{gameView.game === 'okey101' && (
				<Okey101Scores
					view={gameView}
					summary={summary}
					nameOf={nameOf}
					data-flx="table-game.okey.okey-board.okey101-scores"
				/>
			)}
		</div>
	);
});
